import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./index.module.css";
import Skeleton from "../../../components/Skeleton";
import NoResultsFound from "../../../components/NoResultsFound";
import Modal from "../../../components/Modal";
import { showToast } from "../../../components/ToastMsg";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { editUser, getAllUsers } from "../../../redux/features/users/userService";
import SearchBar from "../../../components/SearchBar";
import Pagination from "../../../components/Pagination";
import SearchBarOnChange from "../../../components/SearchBarOnChange";

const Users = () => {
  const dispatch = useDispatch();

  const { isUsersLoading, pageCount, users } = useSelector(
    (state) => state.user.allUsers
  );

  const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
  const [isActivateModalOpen, setActivationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [skip, setSkip] = useState(0);

  const limit = 10;

  const closeDeactivationModal = () => setDeactivationModalOpen(false);
  const closeActivationModal = () => setActivationModalOpen(false);

  const fetchAllUsers = useCallback(() => {
    dispatch(getAllUsers({
      skip: skip,
      limit,
      search: searchQuery,
    }));
  }, [dispatch, skip, searchQuery]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const handleDeactivate = async () => {
    setDeactivationModalOpen(false);
    try {
      const response = await dispatch(
        editUser({ id: selectedUser.id, status: 'Deactivated' })
      ).unwrap();
      setSelectedUser(null);
      showToast("success", response.message);
      fetchAllUsers();
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const handleActivate = async () => {
    setActivationModalOpen(false);
    try {
      const response = await dispatch(
        editUser({ id: selectedUser.id, status: 'Active' })
      ).unwrap();
      setSelectedUser(null);
      showToast("success", response.message);
      fetchAllUsers();
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const handlePageChange = (pageNumber) => {
    setSkip(pageNumber - 1);
  };

  const handleSearch = (query) => {
    setSearchQuery(query)
    setSkip(0);
  };

  return (
    <div className={`card-section`}>
      <div className={`search-card`}>
        <div className={`search-card-header`}>
          <h1 className={`form-title ${styles.headDiv}`}>
            Users
          </h1>
          <SearchBarOnChange
            value={searchQuery}
            onSearch={handleSearch}
          />
        </div>
        <div className={`search-card-body`}>
          {isUsersLoading ? (
            <Skeleton
              columns={["Full Name", "Phone Number", "Email", "Status", "Actions"]}
              rowCount={10}
              height={20}
            />
          ) : users.length > 0 ? (
            <div className={`table-container`}>
              <table className={`table`}>
                <thead className={`thead`}>
                  <tr>
                    <th className={`theadColumnOne`}>Full Name</th>
                    <th>Phone Number</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className={`tbody`}>
                  {users?.map((user) => (
                    <tr key={user.id}>
                      <td className={`tdataColumnOne`}>
                        {user.full_name}
                      </td>
                      <td>{user.phone_number}</td>
                      <td>{user.email}</td>
                      <td>{user.status === '' ? 'Pending' : user.status}</td>
                      <td className={`actionsBtn`}>
                        {user.status === 'Active' &&
                          <button className={`btn-inactive`} onClick={() => {
                            setDeactivationModalOpen(true);
                            setSelectedUser(user);
                          }}>
                            Deactivate
                          </button>
                        }
                        {(user.status !== 'Active' && user.status !== 'Pending') &&
                          <button className={`btn-active`} onClick={() => {
                            setActivationModalOpen(true);
                            setSelectedUser(user);
                          }}>
                            Activate
                          </button>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoResultsFound />
          )}
        </div>
        <div className={`search-card-footer`}>
          {pageCount > 0 && (
            <div className={styles.paginationContainer}>
              <Pagination
                currentPage={skip + 1}
                totalPages={pageCount}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      {isDeactivationModalOpen && (
        <Modal
          title="User Deactivation"
          onClose={closeDeactivationModal}
          onYes={handleDeactivate}
          isInput={false}
          iconName={faCircleXmark}
          bodyText={`Are you sure you want to deactivate the user? ${selectedUser.username}`}
          colorType={`var(--danger)`}
        />
      )}
      {isActivateModalOpen && (
        <Modal
          title="User Activation"
          onClose={closeActivationModal}
          onYes={handleActivate}
          isInput={false}
          iconName={faCircleCheck}
          bodyText={`Are you sure you want to activate the user? ${selectedUser.username}`}
          colorType={`var(--green)`}
        />
      )}
    </div>
  );
};

export default Users;
